import { Timestamp } from "@/components/Timestamp";
import { cn } from "@/lib/utils";
import { formatVersionLabel, type CanvasVersionListItem } from "@/pages/app/lib/canvas-versions";
import { useCallback } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { RUNS_SIDEBAR_ROW_CLASS } from "./runsSidebarRowLayout";

export function VersionRow({
  version,
  isActive = false,
  isCurrentLive = false,
  isFirstCanvasVersion = false,
  rowTestId,
  onUseVersion,
}: {
  version: CanvasVersionListItem;
  isActive?: boolean;
  isCurrentLive?: boolean;
  isFirstCanvasVersion?: boolean;
  rowTestId?: string;
  onUseVersion: (versionID: string) => void;
}) {
  const { versionID, ownerName, versionLabel, timestamp } = deriveVersionRowFields(version, isFirstCanvasVersion);

  const handleRowActivate = useCallback(() => {
    onUseVersion(versionID);
  }, [onUseVersion, versionID]);

  const handleRowKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!isActivationKey(event.key)) return;
      event.preventDefault();
      onUseVersion(versionID);
    },
    [onUseVersion, versionID],
  );

  if (!versionID) {
    return null;
  }

  return (
    <div
      data-testid={rowTestId}
      className={versionRowClassName(isActive)}
      role="button"
      tabIndex={0}
      onClick={handleRowActivate}
      onKeyDown={handleRowKeyDown}
      aria-label={`Preview ${versionLabel}`}
      title={`${versionLabel} · ${ownerName}`}
    >
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-xs",
          isActive
            ? "font-semibold text-sky-900 dark:text-indigo-300"
            : "font-medium text-slate-900 dark:text-gray-100",
        )}
      >
        {versionLabel}
      </span>
      {isCurrentLive ? (
        <span className="shrink-0 rounded bg-sky-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-sky-800 dark:bg-gray-700 dark:text-indigo-300">
          Current
        </span>
      ) : null}
      <span className="max-w-[40%] shrink-0 truncate text-[11px] text-slate-500 dark:text-gray-400">{ownerName}</span>
      {timestamp ? (
        <Timestamp
          date={timestamp}
          display="relative"
          relativeStyle="abbreviated"
          includeAgo={false}
          className="shrink-0 text-xs tabular-nums text-slate-500 dark:text-gray-400"
        />
      ) : null}
    </div>
  );
}

function deriveVersionRowFields(version: CanvasVersionListItem, isFirstCanvasVersion: boolean) {
  return {
    versionID: version.id ?? "",
    ownerName: version.owner?.name || "Unknown owner",
    versionLabel: isFirstCanvasVersion ? "v1" : formatVersionLabel(version),
    timestamp: version.updatedAt || version.createdAt,
  };
}

function isActivationKey(key: string): boolean {
  return key === "Enter" || key === " ";
}

function versionRowClassName(isActive: boolean): string {
  return cn(
    RUNS_SIDEBAR_ROW_CLASS,
    "group w-full cursor-pointer text-left transition-colors",
    isActive ? "bg-sky-100 dark:bg-gray-800" : "bg-white hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800",
  );
}
